// =============================================================================
// videoDocuments.js : règles PURES du lecteur des vidéos d'une liste de
// documents SharePoint (demande de Pierre du 20/10/2026 : « pourquoi on ne
// peut pas "jouer" la vidéo dans un player sur le site ? »). Le composant est
// src/LecteurVideoDocument.js, chargé à la demande ; ce fichier, sans React,
// voyage avec les listes (DocumentLibrary, guichet des pièces d'une séance,
// pièces d'un point de l'ordre du jour).
// -----------------------------------------------------------------------------
// - La source est l'URL pré-authentifiée de téléchargement que le serveur
//   remet déjà (getDownloadUrl, getSessionDocUrl : environ une heure), après
//   avoir relu le droit de l'appelant. Le lecteur ne la garde que dans `src` :
//   ni base, ni journal, ni stockage du navigateur, ni lien partagé.
// - SharePoint sert les plages (Range) : la lecture démarre et l'avance
//   rapide saute sans tout télécharger, index (moov) en tête ou en fin.
// - Rien n'est transcodé : un format que le navigateur ne décode pas (HEVC
//   d'un iPhone dans Chrome ou Firefox, par exemple) se DIT, et la vidéo se
//   télécharge.
// Nom de fichier distinct du composant : macOS ne distingue pas
// « lecteurVideoDocument.js » de « LecteurVideoDocument.js ».
// =============================================================================

/** Extensions lues dans la page (le navigateur décide du reste). */
export const VIDEO_RX = /\.(mp4|mov|m4v|webm)$/i;
export const estVideo = (nom) => VIDEO_RX.test(String(nom || "").trim());

export const MESSAGE_FORMAT = "Ce navigateur ne sait pas lire ce format : téléchargez la vidéo ou ouvrez-la dans Safari.";
// Safari (et tout navigateur d'iPhone ou d'iPad, tous WebKit) : l'inviter à
// « ouvrir dans Safari » n'aurait pas de sens.
export const MESSAGE_FORMAT_WEBKIT = "Ce navigateur ne sait pas lire ce format : téléchargez la vidéo pour l'ouvrir avec une autre application.";
// Une vidéo en lecture seule (replay d'une formation, 26/10/2026).
export const MESSAGE_FORMAT_LECTURE_SEULE = "Ce navigateur ne sait pas lire ce format : ouvrez la page dans un autre navigateur (Safari, Chrome ou Edge), ou demandez à l'organisateur de la session.";
export const MESSAGE_RESEAU = "La vidéo ne se lit pas pour l'instant : vérifiez la connexion, puis réessayez.";
// Politique de sécurité du site (CSP, `media-src` de vercel.json) qui refuse
// l'hôte servi : le dire, plutôt qu'un faux « format illisible ».
export const MESSAGE_BLOQUE = "La lecture est refusée par la politique de sécurité du site : téléchargez la vidéo, et signalez-le à l'équipe d'Enio.";

/** Message d'un lien que le serveur n'a pas remis : son refus (droit relu,
 *  session à renouveler) se dit tel quel, tout le reste est « la connexion ». */
export function messageEchecLien(e) {
  const m = String((e && e.message) || "");
  return /^(Accès refusé|Session à renouveler)/.test(m) ? m : MESSAGE_RESEAU;
}

/** Safari, ou un navigateur d'iPhone ou d'iPad (WebKit imposé, application
 *  installée comprise) ? Lu sur l'agent utilisateur, pour le message seul. */
export function estWebKitApple(ua) {
  const u = String(ua || "");
  if (/iPhone|iPad|iPod/.test(u)) return true;
  return /Macintosh/.test(u) && /Safari\//.test(u) && !/Chrome|Chromium|Edg\/|Firefox|OPR\//.test(u);
}
export const messageFormat = (ua) => (estWebKitApple(ua) ? MESSAGE_FORMAT_WEBKIT : MESSAGE_FORMAT);

/** Un refus de la CSP concerne-t-il la lecture (directive media-src) ? */
export const refusLecture = (e) => /^media-src\b/.test(String((e && (e.effectiveDirective || e.violatedDirective)) || ""));

/** Durée de vie prudente d'un lien dont l'échéance ne se lit pas. */
export const DUREE_LIEN_PRUDENTE_MS = 50 * 60 * 1000;

/**
 * Échéance (ms) d'un lien de téléchargement SharePoint : champ `exp` (en
 * secondes, nombre ou chaîne) de la charge du jeton `tempauth`. Illisible :
 * `obtenuLe` + 50 minutes. Un lien `blob:` (octets remis par le serveur, repli
 * rare) n'expire pas : null.
 */
export function echeanceLien(url, obtenuLe) {
  const s = String(url || "");
  if (s.startsWith("blob:")) return null;
  const repli = Number.isFinite(Number(obtenuLe)) && Number(obtenuLe) > 0 ? Number(obtenuLe) + DUREE_LIEN_PRUDENTE_MS : null;
  let jeton = null;
  try { jeton = new URL(s).searchParams.get("tempauth"); } catch (_) { return repli; }
  if (!jeton) return repli;
  const parts = jeton.split(".");
  if (parts.length < 2) return repli;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const exp = Number(JSON.parse(atob(b64 + "===".slice((b64.length + 3) % 4))).exp);
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : repli;
  } catch (_) {
    return repli;
  }
}

// Codes de MediaError (HTMLMediaElement.error.code).
export const ERREUR_INTERROMPUE = 1;
export const ERREUR_RESEAU = 2;
export const ERREUR_DECODAGE = 3;
export const ERREUR_SOURCE = 4;

/**
 * Que faire d'une erreur de lecture ?
 *   code            : MediaError.code (1 à 4) ;
 *   metaVue         : les métadonnées de la vidéo ont-elles été lues une fois
 *                     (le navigateur a su ouvrir le fichier) ?
 *   essaisAvantMeta : nouveaux liens déjà demandés sans métadonnées lues ;
 *   relien          : décision des liens échus (apresErreurLecture de la
 *                     Médiathèque : trois nouveaux liens par dix minutes),
 *                     "relien" ou "abandon".
 * Avant toute métadonnée, Chrome rend le même code 4 pour un format inconnu
 * et pour un lien refusé (403) : un nouveau lien d'abord, une fois ; s'il
 * échoue encore, c'est le format. Une erreur de DÉCODAGE (3) est le format.
 * Après des métadonnées lues : lien échu pendant une pause ou coupure, nouveau
 * lien et reprise à la position atteinte, jusqu'à la limite (réseau).
 *   → "ignorer" | "relien" | "format" | "reseau"
 */
export function decisionErreur({ code, metaVue = false, essaisAvantMeta = 0, relien = "relien" } = {}) {
  const c = Number(code);
  if (c === ERREUR_INTERROMPUE) return "ignorer";
  if (c === ERREUR_DECODAGE) return "format";
  if (!metaVue) {
    if (Number(essaisAvantMeta) < 1 && relien !== "abandon") return "relien";
    return c === ERREUR_RESEAU ? "reseau" : "format";
  }
  return relien === "abandon" ? "reseau" : "relien";
}

/** Métadonnées lues mais aucune image (largeur et hauteur nulles) : le
 *  navigateur ne décode pas la piste vidéo (Chrome joue alors le son seul). */
export const imageIllisible = (v) => !!v && Number(v.videoWidth) === 0 && Number(v.videoHeight) === 0;

/** Taille affichée dans la fenêtre (« 2,8 Go »). */
export function tailleLisible(o) {
  const n = Number(o);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} Ko`;
  if (n < 1024 * 1024 * 1024) return `${(Math.round(n / 1024 / 1024 * 10) / 10).toLocaleString("fr-FR")} Mo`;
  return `${(Math.round(n / 1024 / 1024 / 1024 * 10) / 10).toLocaleString("fr-FR")} Go`;
}
